/* Admin: Gallery — add/edit/delete images grouped by edition tag. */
import { useState } from "react";
import AdminShell from "../../components/shell/AdminShell.jsx";
import {
  Button, Field, Input, Card, Modal, EmptyState, Skeleton, ConfirmDialog,
  Icon, SmartImage, MediaUploaderField, showToast,
} from "../../ui/index.js";
import { useGallery } from "../../data/hooks.js";
import { saveGalleryItem, deleteGalleryItem } from "../../data/mutations.js";

export default function AdminGalleryPage() {
  const { gallery, loading } = useGallery();
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const grouped = gallery.reduce((acc, item) => {
    (acc[item.editionTag] = acc[item.editionTag] || []).push(item);
    return acc;
  }, {});
  const editions = Object.keys(grouped).sort().reverse();

  const onDelete = async () => {
    try {
      await deleteGalleryItem(deleting.id, deleting.image?.storagePath);
      showToast({ intent: "success", title: "Image removed" });
    } catch (err) {
      showToast({ intent: "danger", title: "Couldn't delete", body: err.message });
    }
    setDeleting(null);
  };

  return (
    <AdminShell>
      <div className="admin-main__header">
        <div><h1 className="t-3xl">Gallery</h1><p>Photos grouped by edition / programme tag.</p></div>
        <Button variant="primary" onClick={() => setEditing({ editionTag: editions[0] || "2026", order: 1, image: null, caption: "" })}><Icon.Plus size={16} />Add image</Button>
      </div>

      {loading ? (
        <Skeleton h={240} />
      ) : gallery.length === 0 ? (
        <Card><EmptyState icon={<Icon.Images size={40} />} title="No gallery items yet." body="Photos will appear here." action={<Button variant="primary" onClick={() => setEditing({ editionTag: "2026", order: 1, image: null, caption: "" })}>Add your first image</Button>} /></Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 40 }}>
          {editions.map((edition) => (
            <div key={edition}>
              <div className="row row--between" style={{ marginBottom: 16 }}>
                <h2 className="t-2xl" style={{ margin: 0 }}>{edition} <span className="t-sm muted" style={{ marginLeft: 8, fontWeight: 400 }}>{grouped[edition].length} photos</span></h2>
                <Button variant="ghost" size="sm" onClick={() => setEditing({ editionTag: edition, order: grouped[edition].length + 1, image: null, caption: "" })}><Icon.Plus size={14} />Add to this edition</Button>
              </div>
              <div className="admin-gallery-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
                {grouped[edition].map((item) => (
                  <div key={item.id} className="card" style={{ padding: 12, position: "relative" }}>
                    <div style={{ aspectRatio: "1 / 1", borderRadius: "var(--radius)", overflow: "hidden", background: "var(--bg-warm)", marginBottom: 8 }}>
                      <SmartImage src={item.image?.url} alt={item.image?.alt} label={item.caption} style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }} />
                    </div>
                    <div className="t-xs muted" style={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>{item.caption || "Untitled"}</div>
                    <div style={{ display: "flex", gap: 4, position: "absolute", top: 16, right: 16 }}>
                      <Button variant="secondary" size="sm" onClick={() => setEditing(item)} aria-label="Edit"><Icon.Pencil size={12} /></Button>
                      <Button variant="secondary" size="sm" onClick={() => setDeleting(item)} aria-label="Delete"><Icon.Trash size={12} /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && <GalleryItemModal item={editing} editions={editions} onClose={() => setEditing(null)} />}
      <ConfirmDialog open={!!deleting} title="Delete this image?" body="This will remove it from the public gallery." confirmLabel="Delete" destructive onConfirm={onDelete} onClose={() => setDeleting(null)} />
    </AdminShell>
  );
}

function GalleryItemModal({ item, editions, onClose }) {
  const [form, setForm] = useState({
    image: item.image || null,
    caption: item.caption || "",
    editionTag: item.editionTag || "",
    order: item.order || 1,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const e = {};
    if (!form.image?.url) e.image = "Please add an image.";
    if (!form.image?.alt?.trim()) e.image = "Add a description (alt text) on the image.";
    if (!form.editionTag.trim()) e.editionTag = "Edition tag is required.";
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      await saveGalleryItem(item.id || null, { image: form.image, caption: form.caption, editionTag: form.editionTag.trim(), order: Number(form.order) });
      showToast({ intent: "success", title: item.id ? "Image updated" : "Image added" });
      onClose();
    } catch (err) {
      showToast({ intent: "danger", title: "Couldn't save", body: err.message });
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} size="lg" title={item.id ? "Edit gallery image" : "Add a gallery image"}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button><Button variant="primary" onClick={submit} loading={saving}>Save</Button></>}>
      <div className="stack-lg">
        <Field label="Image" required error={errors.image}>
          <MediaUploaderField value={form.image} onChange={(v) => setForm((f) => ({ ...f, image: v }))} path={`gallery/${form.editionTag || "untagged"}`} aspect="4/3" />
        </Field>
        <Field label="Caption (optional)"><Input value={form.caption} onChange={(e) => setForm((f) => ({ ...f, caption: e.target.value }))} /></Field>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
          <Field label="Edition / tag" required helper="Use an existing tag or create a new one." error={errors.editionTag}>
            <Input value={form.editionTag} onChange={(e) => setForm((f) => ({ ...f, editionTag: e.target.value }))} list="edition-list" error={!!errors.editionTag} />
            <datalist id="edition-list">{editions.map((e) => <option key={e} value={e} />)}</datalist>
          </Field>
          <Field label="Order" helper="Lower numbers first."><Input type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: Number(e.target.value) }))} /></Field>
        </div>
      </div>
    </Modal>
  );
}
